import Head from "next/head";
import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowDown,
  ArrowRight,
  BrainCircuit,
  CandlestickChart,
  ChevronDown,
  Database,
  GitMerge,
  Layers,
  LayoutTemplate,
  PlayCircle,
  Search,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  useGetActions,
  useGetDatas,
  useGetIndicators,
  useGetLogics,
  useGetPatterns,
  useGetChartPatterns,
} from "@/api/canvasApi";
import Link from "next/link";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import { useRouter } from "next/router";
import { useRecoilState } from "recoil";
import { guideModalAtom } from "@/Atoms/modal/DialogueAtoms";

// --- Components ---

const SectionHeader = ({
  title,
  subtitle,
  icon: Icon,
}: {
  title: string;
  subtitle?: React.ReactNode;
  icon?: React.ElementType;
}) => (
  <div className="mb-12 text-center">
    {Icon && (
      <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[color-mix(in_srgb,var(--accent-primary)_10%,transparent)] text-[var(--accent-primary)] shadow-[0_0_20px_-5px_var(--accent-primary)]">
        <Icon size={28} />
      </div>
    )}
    <h2 className="mb-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
      {title}
    </h2>
    {subtitle && (
      <div className="mx-auto max-w-3xl text-lg text-muted-foreground leading-relaxed">
        {subtitle}
      </div>
    )}
  </div>
);

const BlockCard = ({
  title,
  description,
  icon: Icon,
  badge,
  className,
}: {
  title: string;
  description?: string;
  icon?: React.ElementType;
  badge?: string;
  className?: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={cn(
      "group relative flex flex-col rounded-xl border border-border/40 bg-card/60 p-5 backdrop-blur-sm transition-all hover:border-[color-mix(in_srgb,var(--accent-primary)_30%,transparent)] hover:shadow-md hover:shadow-[color-mix(in_srgb,var(--accent-primary)_5%,transparent)]",
      className,
    )}
  >
    <div className="mb-3 flex items-start justify-between">
      {Icon ? (
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary/50 text-foreground transition-colors group-hover:bg-[var(--accent-primary)] group-hover:text-white">
          <Icon size={16} />
        </div>
      ) : (
        <div className="h-8 w-8" />
      )}
      {badge && (
        <Badge
          variant="secondary"
          className="bg-[color-mix(in_srgb,var(--accent-primary)_10%,transparent)] text-[var(--accent-primary)] text-xs font-normal"
        >
          {badge}
        </Badge>
      )}
    </div>
    <h3 className="mb-1.5 text-base font-bold text-foreground tracking-tight">
      {title}
    </h3>
    {description && (
      <p className="text-xs leading-relaxed text-muted-foreground line-clamp-3">
        {description}
      </p>
    )}
  </motion.div>
);

const VideoCard = ({
  title,
  videoUrl,
}: {
  title: string;
  videoUrl: string;
}) => {
  const [, setShowGuideModal] = useRecoilState(guideModalAtom);
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  useEffect(() => {
    const fetchThumbnail = async () => {
      try {
        const videoId = videoUrl.split("/").pop();
        const response = await fetch(
          `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`,
        );
        const data = await response.json();
        if (data.thumbnail_url) {
          setThumbnail(data.thumbnail_url);
        }
      } catch (error) {
        console.error("Error fetching vimeo thumbnail:", error);
      }
    };

    fetchThumbnail();
  }, [videoUrl]);

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => setShowGuideModal({ isOpen: true, link: videoUrl })}
      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-border/40 bg-card/40 backdrop-blur-md transition-all hover:border-[color-mix(in_srgb,var(--accent-primary)_40%,transparent)] hover:shadow-2xl hover:shadow-[color-mix(in_srgb,var(--accent-primary)_10%,transparent)]"
    >
      <div className="aspect-video relative overflow-hidden bg-secondary/20">
        {thumbnail && (
          <img
            src={thumbnail}
            alt={title}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-black/40 to-black/10 transition-colors group-hover:from-black/50 group-hover:to-black/20">
          <div className="flex h-14 w-14 items-center justify-center rounded-full text-[--accent-primary] text-white shadow-lg transition-transform group-hover:scale-110">
            <PlayCircle size={28} />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-black/40 p-4 backdrop-blur-sm">
          <h4 className="text-sm font-bold text-white tracking-wide">
            {title}
          </h4>
        </div>
      </div>
    </motion.div>
  );
};

const VideoGuide = () => (
  <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto w-full px-4">
    <VideoCard
      title="Building Strategies - Part 1"
      videoUrl="https://player.vimeo.com/video/1178408562"
    />
    <VideoCard
      title="Building Strategies - Part 2"
      videoUrl="https://player.vimeo.com/video/1178408317"
    />
  </div>
);

const FeatureList = ({ items, title }: { items: string[]; title?: string }) => (
  <div className="rounded-2xl border border-border/40 bg-secondary/5 p-6 backdrop-blur-md">
    {title && (
      <h4 className="mb-4 font-semibold text-base text-foreground tracking-tight">
        {title}
      </h4>
    )}
    <ul className="space-y-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <div className="mt-1.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--accent-primary)_10%,transparent)] text-[var(--accent-primary)]">
            <ArrowRight size={10} strokeWidth={3} />
          </div>
          <span className="text-sm text-muted-foreground leading-relaxed">
            {item}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

// --- Helpers ---

const filterBlocks = (blocks: any[], searchQuery: string) => {
  if (!searchQuery) return blocks;
  const q = searchQuery.toLowerCase();
  return blocks.filter((block) => {
    const name = block.label || block.name || "";
    const desc = block.description || "";
    return name.toLowerCase().includes(q) || desc.toLowerCase().includes(q);
  });
};

const BlockSection = ({
  title,
  blocks,
  icon: Icon,
  isLoading,
  description,
}: {
  title: string;
  blocks: any[];
  icon?: React.ElementType;
  isLoading: boolean;
  description?: React.ReactNode;
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredBlocks = useMemo(
    () => filterBlocks(blocks, searchQuery),
    [blocks, searchQuery],
  );

  return (
    <div className="w-full max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-2">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="text-[var(--accent-primary)]" size={24} />}
          <h3 className="font-bold text-2xl">{title}</h3>
          <Badge variant="outline" className="ml-2">
            {blocks.length} Total
          </Badge>
        </div>
        <div className="relative w-full sm:w-72">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            <Search size={16} />
          </div>
          <input
            type="text"
            placeholder={`Search ${title}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-10 pl-9 pr-4 rounded-lg border border-border/50 bg-background/50 backdrop-blur-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] transition-all text-sm"
          />
        </div>
      </div>

      {description && (
        <div className="px-2 text-muted-foreground leading-relaxed text-base">
          {description}
        </div>
      )}

      {isLoading ? (
        <div className="p-8 text-center text-muted-foreground">Loading...</div>
      ) : filteredBlocks.length === 0 && searchQuery ? (
        <div className="p-4 text-center text-muted-foreground text-sm border border-dashed border-border/50 rounded-xl">
          No matches found for "{searchQuery}".
        </div>
      ) : filteredBlocks.length === 0 ? (
        <div className="p-4 text-center text-muted-foreground text-sm border border-dashed border-border/50 rounded-xl">
          No blocks available.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
          {filteredBlocks.map((block: any, i: number) => (
            <BlockCard
              key={i}
              title={block.label || block.name || "Unknown"}
              description={block.description}
              icon={Icon}
              badge={block.category || "General"}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// --- Sections ---

const HeroSection = () => (
  <section className="relative overflow-hidden pt-32 pb-20">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[color-mix(in_srgb,var(--accent-primary)_5%,transparent)] rounded-full blur-[120px] pointer-events-none" />
    <div className="container relative z-10 px-4 md:px-6">
      <div className="mx-auto max-w-4xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-6 flex justify-center">
            <Badge
              variant="outline"
              className="border-[color-mix(in_srgb,var(--accent-primary)_30%,transparent)] text-[var(--accent-primary)] py-1.5 px-4 text-sm"
            >
              Strategy Builder Guide
            </Badge>
          </div>
          <h1 className="mb-6 text-4xl font-extrabold tracking-tight text-foreground sm:text-6xl">
            A Beginner’s Guide to <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--accent-primary)] to-[color-mix(in_srgb,var(--accent-primary)_60%,white)]">
              Building Trading Strategies with Algo Studio
            </span>
          </h1>
          <p className="mx-auto max-w-2xl text-xl text-muted-foreground leading-relaxed mb-4">
            This guide explains how strategies are built using a simple, modular
            block-based approach. Understanding these blocks — and how they
            connect — is the key to creating reliable, testable, and automated
            trading strategies.
          </p>
          <VideoGuide />
        </motion.div>
      </div>
    </div>
  </section>
);

const BlockPhilosophySection = () => (
  <section className="py-16 border-y border-border/40 bg-secondary/5">
    <div className="container px-4 md:px-6">
      <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
        <div>
          <h2 className="mb-6 text-3xl font-bold tracking-tight text-foreground">
            The Block-Building Philosophy
          </h2>
          <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
            <p>
              Strategies are built visually using connected blocks, rather than
              writing code. Each block represents a single responsibility within
              a strategy, and strategies are created by linking these blocks
              together in a logical flow.
            </p>
            <p>This approach mirrors how professional traders think:</p>
            <ul className="space-y-2 list-disc pl-5 text-base">
              <li>
                <strong>Analyze</strong> the market
              </li>
              <li>
                <strong>Decide</strong> if conditions are met
              </li>
              <li>
                <strong>Take action</strong>.
              </li>
            </ul>
            <p>
              By separating these steps into different block types, this ensures
              strategies are:
            </p>
            <ul className="space-y-2 list-disc pl-5 text-base">
              <li>
                <strong>Transparent</strong> – You can see exactly why a trade
                occurs
              </li>
              <li>
                <strong>Easy to debug</strong> – Issues are isolated to specific
                blocks
              </li>
              <li>
                <strong>Safe to automate</strong> – No hidden or accidental
                trade execution
              </li>
            </ul>
          </div>
        </div>
        <div className="bg-card/50 border border-border/40 rounded-2xl p-8 backdrop-blur-sm">
          <h3 className="font-bold text-xl mb-6 text-center">
            The Core Block Types
          </h3>
          <p className="text-muted-foreground text-center mb-6">
            Every strategy is built using four distinct categories of blocks.
            Each category has a clear role, and blocks must be connected in this
            order.
          </p>
          <div className="space-y-4">
            {[
              {
                label: "Data Blocks",
                desc: "Provide raw market values",
                icon: Database,
              },
              {
                label: "Indicator Blocks",
                desc: "Analyze market behavior",
                icon: LayoutTemplate,
              },
              {
                label: "Logic Blocks",
                desc: "Evaluate conditions",
                icon: GitMerge,
              },
              {
                label: "Action Blocks",
                desc: "Execute decisions",
                icon: PlayCircle,
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-4 p-3 rounded-lg hover:bg-secondary/40 transition-colors"
              >
                <div
                  className={cn(
                    "h-10 w-10 flex items-center justify-center rounded-md font-bold bg-[color-mix(in_srgb,var(--accent-primary)_10%,transparent)] text-[var(--accent-primary)]",
                  )}
                >
                  <item.icon size={20} />
                </div>
                <div>
                  <div className="font-bold text-foreground">{item.label}</div>
                  <div className="text-sm text-muted-foreground">
                    {item.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const AllBlocksSection = () => {
  const { data: dataBlocks, isLoading: loadData } = useGetDatas();
  const { data: indBlocks, isLoading: loadInd } = useGetIndicators();
  const { data: logicBlocks, isLoading: loadLogic } = useGetLogics();
  const { data: actionBlocks, isLoading: loadAction } = useGetActions();

  return (
    <section className="py-20 space-y-24">
      <div className="container px-4 md:px-6">
        <BlockSection
          title="1. Data Blocks"
          blocks={dataBlocks?.data || []}
          icon={Database}
          isLoading={loadData}
          description={
            <div className="space-y-4">
              <p>
                <strong>Raw Market Inputs.</strong> Data blocks provide direct
                price data from the market. They are the most basic building
                blocks and often serve as the starting point for strategies.
              </p>
              <p>
                Unlike indicator blocks, data blocks do not perform
                calculations. They simply expose real-time or historical price
                values that can be used directly in logic blocks or as inputs to
                indicators.
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-sm mt-4 p-4 bg-secondary/10 rounded-xl border border-border/40">
                <div>
                  <strong>What Data Blocks Do:</strong>
                  <ul className="list-disc pl-4 mt-2 space-y-1">
                    <li>The current traded price</li>
                    <li>Candle-based price values</li>
                    <li>High-level price comparisons</li>
                  </ul>
                </div>
                <div>
                  <strong>Commonly Used For:</strong>
                  <ul className="list-disc pl-4 mt-2 space-y-1">
                    <li>Simple price-based strategies</li>
                    <li>Breakout and reversal logic</li>
                    <li>Comparing price to indicators</li>
                  </ul>
                </div>
              </div>
              <p className="text-sm italic text-muted-foreground/80">
                Tip: Data blocks can connect directly to logic blocks or feed
                into indicator blocks for further analysis.
              </p>
            </div>
          }
        />
      </div>

      <div className="container px-4 md:px-6">
        <BlockSection
          title="2. Indicator Blocks"
          blocks={indBlocks?.data || []}
          icon={LayoutTemplate}
          isLoading={loadInd}
          description={
            <div className="space-y-4">
              <p>
                <strong>For Market Analysis.</strong> Indicator blocks are
                responsible for reading and interpreting market data. They do
                not make decisions or place trades — they simply calculate
                values that describe what the market is doing.
              </p>
              <p>
                These values (Price movement, Volume, Volatility, Trend
                direction) are then passed downstream to logic blocks for
                evaluation.
              </p>
              <div className="p-4 bg-secondary/10 rounded-xl border border-border/40">
                <strong>Typical Use Cases:</strong>
                <ul className="list-disc pl-4 mt-2 space-y-1 text-sm">
                  <li>Identifying trends (e.g. moving averages)</li>
                  <li>Measuring momentum (e.g. RSI, MACD)</li>
                  <li>Detecting volatility changes</li>
                  <li>Tracking volume behavior</li>
                </ul>
              </div>
              <p className="text-sm italic text-muted-foreground/80">
                Note: Indicator blocks never execute trades directly. They must
                always connect to logic blocks to be meaningful.
              </p>
            </div>
          }
        />
      </div>

      <div className="container px-4 md:px-6">
        <BlockSection
          title="3. Logic Blocks"
          blocks={logicBlocks?.data || []}
          icon={GitMerge}
          isLoading={loadLogic}
          description={
            <div className="space-y-4">
              <p>
                <strong>Decision-Making and Rules.</strong> Logic blocks define
                when something is true or false. They act as the decision layer
                of your strategy, evaluating indicator values and determining
                whether conditions are met.
              </p>
              <p>
                Think of logic blocks as questions your strategy asks the
                market. They take inputs, compare values, and output a
                true/false result.
              </p>
              <div className="p-4 bg-secondary/10 rounded-xl border border-border/40">
                <strong>Typical Use Cases:</strong>
                <ul className="list-disc pl-4 mt-2 space-y-1 text-sm">
                  <li>“Price crosses above a moving average”</li>
                  <li>“RSI is higher than 70”</li>
                  <li>“Volume increases by a specific amount”</li>
                </ul>
              </div>
              <p className="text-sm italic text-muted-foreground/80">
                Note: Logic blocks act as gatekeepers. If logic conditions are
                not met, actions will never trigger.
              </p>
            </div>
          }
        />
      </div>

      <div className="container px-4 md:px-6">
        <BlockSection
          title="4. Action Blocks"
          blocks={actionBlocks?.data || []}
          icon={PlayCircle}
          isLoading={loadAction}
          description={
            <div className="space-y-4">
              <p>
                <strong>Executing Trades and Managing Positions.</strong> Action
                blocks define what the strategy actually does once all logic
                conditions are satisfied. This is where decisions turn into real
                market activity.
              </p>
              <p>
                Action blocks can open/close positions, adjust risk settings,
                and manage orders. Smart actions are designed to handle more
                advanced execution logic automatically.
              </p>
              <div className="p-4 border border-[var(--accent-primary)]/20 bg-[var(--accent-primary)]/5 rounded-xl">
                <p className="font-bold text-[var(--accent-primary)] mb-1">
                  Warning
                </p>
                <p className="text-sm">
                  Action blocks are executed in real time during live trading.
                  Always test strategies thoroughly before deploying them live.
                </p>
              </div>
            </div>
          }
        />
      </div>
    </section>
  );
};

const WorkflowSection = () => (
  <section className="bg-secondary/5 py-20 border-y border-border/40">
    <div className="container px-4 md:px-6 w-full max-w-5xl mx-auto text-center">
      <h2 className="text-2xl font-bold mb-6">How Blocks Work Together</h2>
      <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
        A complete strategy always follows this flow. This structure ensures
        that analysis is separated from decisions, and decisions are separated
        from execution.
      </p>
      <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-sm font-medium text-muted-foreground">
        <div className="p-4 bg-card rounded-xl border border-border/50">
          Indicators
        </div>
        <ArrowRight className="hidden md:block" />
        <ArrowDown className="block md:hidden" />
        <div className="p-4 bg-card rounded-xl border border-border/50">
          Logic
        </div>
        <ArrowRight className="hidden md:block" />
        <ArrowDown className="block md:hidden" />
        <div className="p-4 bg-card rounded-xl border border-border/50">
          Actions
        </div>
      </div>
      <p className="mt-6 text-sm text-muted-foreground">
        Example: An indicator calculates a moving average → A logic block checks
        if price crosses above it → An action block opens a long position.
      </p>
    </div>
  </section>
);

const PatternsSection = () => {
  const [activeTab, setActiveTab] = useState<"candlestick" | "chart">(
    "candlestick",
  );

  const { data: patternData, isLoading: loadPat } = useGetPatterns();
  const { data: chartPatternData, isLoading: loadChart } =
    useGetChartPatterns();

  const candlestickPatterns = patternData?.data || [];
  const chartPatterns = chartPatternData?.data || [];

  return (
    <section className="py-24">
      <div className="container px-4 md:px-6">
        <SectionHeader
          title="Pattern Blocks"
          subtitle="Recognising Repeating Market Behaviour."
          icon={CandlestickChart}
        />
        <div className="mx-auto max-w-3xl text-center mb-12 text-muted-foreground leading-relaxed">
          <p className="mb-4">
            Pattern blocks allow strategies to identify well-known price
            patterns that repeat across markets and timeframes. These patterns
            represent common trader behavior and market psychology, such as
            reversals, continuations, and exhaustion moves.
          </p>
          <div className="inline-block p-4 border border-[var(--accent-primary)]/20 bg-[var(--accent-primary)]/5 rounded-xl text-left">
            <p className="text-sm">
              <strong className="text-[var(--accent-primary)]">
                Important:
              </strong>{" "}
              Pattern blocks never place trades directly. They are signals, not
              decisions. They must connect to logic blocks before triggering any
              action.
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-5xl">
          <Tabs defaultValue="candlestick" className="w-full">
            <div className="mb-10 flex justify-center">
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger
                  value="candlestick"
                  onClick={() => setActiveTab("candlestick")}
                >
                  Candlestick Patterns
                </TabsTrigger>
                <TabsTrigger
                  value="chart"
                  onClick={() => setActiveTab("chart")}
                >
                  Chart Patterns
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="w-full">
              <TabsContent value="candlestick" className="mt-0">
                <BlockSection
                  title="Candlestick Patterns"
                  blocks={candlestickPatterns}
                  icon={CandlestickChart}
                  isLoading={loadPat}
                  description={
                    <div className="space-y-4">
                      <p>
                        <strong>Short-Term Price Action Signals.</strong>{" "}
                        Candlestick pattern blocks identify specific candle
                        formations that often indicate short-term reversals or
                        continuation moves. These patterns are especially useful
                        for timing entries and exits.
                      </p>
                      <div className="text-sm">
                        <strong>Typical Use Cases:</strong> Entry confirmation
                        at key levels, Reversal detection, Filtering false
                        breakouts.
                      </div>
                    </div>
                  }
                />
              </TabsContent>
              <TabsContent value="chart" className="mt-0">
                <BlockSection
                  title="Chart Patterns"
                  blocks={chartPatterns}
                  icon={CandlestickChart}
                  isLoading={loadChart}
                  description={
                    <div className="space-y-4">
                      <p>
                        <strong>Structure-Based Market Patterns.</strong> Chart
                        pattern blocks detect larger, multi-candle price
                        structures that reflect broader market behavior. These
                        patterns often indicate continuation, trend reversal, or
                        consolidation phases.
                      </p>
                      <p className="text-sm">
                        Includes: Flag & Pennant Patterns, Harmonic Chart
                        Patterns, and Classic Chart Patterns (Head and
                        Shoulders, etc).
                      </p>
                    </div>
                  }
                />
              </TabsContent>
            </div>
          </Tabs>

          <div className="mt-16 grid gap-8 lg:grid-cols-2">
            <div className="p-8 bg-secondary/5 border border-border/40 rounded-2xl">
              <h3 className="text-xl font-bold mb-4">
                Using Pattern Blocks Effectively
              </h3>
              <p className="text-muted-foreground mb-4">
                Pattern blocks are most powerful when used with confirmation,
                such as:
              </p>
              <ul className="space-y-2 mb-6 text-sm text-muted-foreground list-disc pl-5">
                <li>Trend indicators (moving averages)</li>
                <li>Momentum indicators (RSI, MACD)</li>
                <li>Volume analysis</li>
                <li>Key support and resistance levels</li>
              </ul>
              <div className="p-4 bg-background/50 rounded-xl border border-border/50 text-sm font-medium text-center">
                Pattern Detected → Logic Confirms → Action Executes
              </div>
              <p className="mt-4 text-xs text-muted-foreground/80">
                This layered approach helps reduce false signals and improves
                strategy reliability.
              </p>
            </div>

            <div className="p-8 bg-card/30 border border-border/40 rounded-2xl">
              <h3 className="text-xl font-bold mb-4">
                Why Pattern Blocks Matter
              </h3>
              <p className="text-muted-foreground mb-4">
                Pattern blocks allow traders to:
              </p>
              <ul className="space-y-2 mb-6 text-sm text-muted-foreground list-disc pl-5">
                <li>Encode proven trading concepts visually</li>
                <li>Remove subjective pattern interpretation</li>
                <li>Automate pattern recognition consistently</li>
                <li>Combine price action with indicators and logic</li>
              </ul>
              <p className="text-muted-foreground text-sm">
                By integrating pattern blocks into the block framework, traders
                can turn classic technical analysis into clear, rule-based,
                automated strategies.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const WhyThisMattersSection = () => (
  <section className="py-20 bg-secondary/5 border-t border-border/40">
    <div className="container px-4 md:px-6 text-center">
      <h2 className="text-3xl font-bold mb-10">
        Why This Matters for New Traders
      </h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
        {[
          { title: "Visual Logic", desc: "Learn strategy logic visually" },
          { title: "Safety", desc: "Avoid accidental trade execution" },
          { title: "Confidence", desc: "Build confidence before automating" },
          {
            title: "Scalability",
            desc: "Scale from simple ideas to complex systems",
          },
        ].map((item, i) => (
          <div
            key={i}
            className="p-6 bg-card border border-border/40 rounded-xl"
          >
            <h4 className="font-bold text-lg mb-2">{item.title}</h4>
            <p className="text-muted-foreground">{item.desc}</p>
          </div>
        ))}
      </div>
      <p className="mt-10 text-muted-foreground max-w-2xl mx-auto">
        By mastering how each block type works — and how they connect — traders
        gain full control over how and why their strategies behave.
      </p>
    </div>
  </section>
);

const SupportedBlocksContent = ({
  userLoggedIn,
}: {
  userLoggedIn: boolean;
}) => {
  return (
    <main className="min-h-screen bg-background text-foreground selection:bg-[color-mix(in_srgb,var(--accent-primary)_20%,transparent)] font-sans">
      <HeroSection />
      <BlockPhilosophySection />
      <AllBlocksSection />
      <PatternsSection />
      <WorkflowSection />
      <WhyThisMattersSection />
    </main>
  );
};

export default function SupportedBlocks() {
  const router = useRouter();
  const { details } = useAppDetails();
  return (
    <>
      <Head>
        <title>Supported Strategy Blocks | Level 2</title>
        <meta
          name="description"
          content="A guide to the building blocks of trading strategies: Data, Indicators, Logic, and Actions."
        />
      </Head>
      <div className="flex-shrink-0 3xs:max-w-[250px] p-8">
        <Link
          href={{
            pathname: "/build",
            query: { locale: router.locale || "en" },
          }}
        >
          <img
            src={details?.brokerLogo}
            className="md:max-h-[30px] xs:max-h-[25px] max-h-[25px] sm:max-w-[300px]"
            alt=""
          />
        </Link>
      </div>
      <SupportedBlocksContent userLoggedIn={false} />
    </>
  );
}
